import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { HelmetProvider } from "react-helmet-async";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsOfService from "./pages/TermsOfService";
import DisclosurePolicy from "./pages/DisclosurePolicy";
import Disclaimer from "./pages/Disclaimer";
import BlogPost from "./pages/BlogPost";
import Unsubscribe from "./pages/Unsubscribe";
import ReportIssue from "./pages/ReportIssue";
import RequestBlog from "./pages/RequestBlog";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";
import AuthReturnRestorer from "./components/AuthReturnRestorer";
import BackToTop from "./components/BackToTop";
import InstallPrompt from "./components/InstallPrompt";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import BlogPostsPage from "./pages/admin/BlogPosts";
import EditBlogPost from "./pages/admin/EditBlogPost";
import CategoriesPage from "./pages/admin/Categories";
import ShareAnalytics from "./pages/admin/ShareAnalytics";
import QuizManager from "./pages/admin/QuizManager";
import BulkQuizGenerator from "./pages/admin/BulkQuizGenerator";
import BulkPremiumGuideGenerator from "./pages/admin/BulkPremiumGuideGenerator";
import NewsletterManager from "./pages/admin/NewsletterManager";
import IssueReports from "./pages/admin/IssueReports";
import BlogRequests from "./pages/admin/BlogRequests";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import RoadmapOverview from "./pages/RoadmapOverview";
import RoadmapDayPage from "./pages/RoadmapDayPage";
import VerifyCertificate from "./pages/VerifyCertificate";
import MyLearning from "./pages/MyLearning";
import Account from "./pages/Account";
import PremiumResources from "./pages/PremiumResources";
import ResourceViewer from "./pages/ResourceViewer";
import DigitalProducts from "./pages/admin/DigitalProducts";
import ProductCategories from "./pages/admin/ProductCategories";
import Pricing from "./pages/Pricing";
import CheckoutReturn from "./pages/CheckoutReturn";
import InstallAnalytics from "./pages/admin/InstallAnalytics";
import TeamMembers from "./pages/admin/TeamMembers";
import RolesPermissions from "./pages/admin/RolesPermissions";
import LoginActivity from "./pages/admin/LoginActivity";
import AcceptInvite from "./pages/AcceptInvite";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <HelmetProvider>
        <TooltipProvider>
          <BrowserRouter>
          <Toaster />
          <Sonner />
          <ScrollToTop />
          <AuthReturnRestorer />
          <BackToTop />
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/disclosure" element={<DisclosurePolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/:slug/roadmap" element={<RoadmapOverview />} />
            <Route path="/blog/:slug/roadmap/day/:dayNumber" element={<RoadmapDayPage />} />
            <Route path="/verify/:certificateId" element={<VerifyCertificate />} />
            <Route path="/my-learning" element={<MyLearning />} />
            <Route path="/account" element={<Account />} />
            <Route path="/premium-resources" element={<PremiumResources />} />
            <Route path="/resource/:productId/view" element={<ResourceViewer />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/report-issue" element={<ReportIssue />} />
            <Route path="/request-blog" element={<RequestBlog />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/checkout/return" element={<CheckoutReturn />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/posts"
              element={
                <ProtectedRoute>
                  <BlogPostsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/posts/:id"
              element={
                <ProtectedRoute>
                  <EditBlogPost />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedRoute>
                  <CategoriesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute>
                  <ShareAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/install-analytics"
              element={
                <ProtectedRoute>
                  <InstallAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/quiz/:blogPostId"
              element={
                <ProtectedRoute>
                  <QuizManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bulk-quiz-generator"
              element={
                <ProtectedRoute>
                  <BulkQuizGenerator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/bulk-premium-guide-generator"
              element={
                <ProtectedRoute>
                  <BulkPremiumGuideGenerator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/newsletter"
              element={
                <ProtectedRoute>
                  <NewsletterManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/issue-reports"
              element={
                <ProtectedRoute>
                  <IssueReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blog-requests"
              element={
                <ProtectedRoute>
                  <BlogRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/digital-products"
              element={
                <ProtectedRoute>
                  <DigitalProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/product-categories"
              element={
                <ProtectedRoute>
                  <ProductCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/team"
              element={
                <ProtectedRoute>
                  <TeamMembers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/team/roles"
              element={
                <ProtectedRoute>
                  <RolesPermissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/team/activity"
              element={
                <ProtectedRoute>
                  <LoginActivity />
                </ProtectedRoute>
              }
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
      </HelmetProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
